/**
 * Workspace Switcher component
 * Displays list of workspaces with ability to switch between them
 */

import { useTranslation } from 'react-i18next';
import { DropdownLabel, DropdownItem, DropdownSeparator } from './Dropdown';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: string;
  isActive: boolean;
}

interface WorkspaceSwitcherProps {
  workspaces: Workspace[];
  onSwitchWorkspace: (workspaceId: string) => void;
  onCreateWorkspace: () => void;
  isLoading?: boolean;
}

export default function WorkspaceSwitcher({
  workspaces,
  onSwitchWorkspace,
  onCreateWorkspace,
  isLoading = false,
}: WorkspaceSwitcherProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
        {t('userMenu.workspaces.loading', 'Loading workspaces...')}
      </div>
    );
  }

  return (
    <div>
      <DropdownLabel>{t('userMenu.workspaces.label', 'Workspaces')}</DropdownLabel>

      {workspaces.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          {t('userMenu.workspaces.empty', 'No workspaces found')}
        </div>
      ) : (
        workspaces.map((workspace) => (
          <DropdownItem
            key={workspace.id}
            onSelect={() => {
              if (!workspace.isActive) {
                onSwitchWorkspace(workspace.id);
              }
            }}
            disabled={workspace.isActive}
            className={workspace.isActive ? 'bg-gray-100 dark:bg-gray-800 font-bold' : ''}
          >
            <div className="flex flex-col flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate">{workspace.name}</span>
                {workspace.isActive && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-mono">/{workspace.slug}</span>
                <span>•</span>
                <span className="capitalize">{workspace.role}</span>
              </div>
            </div>
          </DropdownItem>
        ))
      )}

      <DropdownSeparator />

      <DropdownItem onSelect={onCreateWorkspace}>
        <span className="font-medium">+ {t('userMenu.workspaces.create', 'Create workspace')}</span>
      </DropdownItem>
    </div>
  );
}
